import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { formatCurrency } from '../utils/currency';
import { toast } from '../components/Toast';
import './FinancialTransactions.css';
import './CashFlow.css';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const TYPE_LABELS = { INCOME: 'Receipt', EXPENSE: 'Expense', PURCHASE: 'Purchase', PAYMENT: 'Payment', ADVANCE: 'Advance' };
const TYPE_COLOR  = { INCOME: '#28a745', EXPENSE: '#dc3545', PURCHASE: '#fd7e14', PAYMENT: '#17a2b8', ADVANCE: '#6f42c1' };
const ICON = { Cash: '💵', Bank: '🏦', UPI: '📲', Cheque: '🧾' };
const accountIcon = (name) => {
  if (!name) return '🏦';
  const n = name.toLowerCase();
  if (n.includes('cash')) return '💵';
  if (n.includes('upi')) return '📲';
  if (n.includes('cheque')) return '🧾';
  return '🏦';
};

function CashFlow() {
  const [balances, setBalances]           = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [accountHeads, setAccountHeads]   = useState([]);
  const [selectedAccount, setSelected]    = useState(null);
  const [loading, setLoading]             = useState(true);
  const [txnLoading, setTxnLoading]       = useState(false);
  const [showAddModal, setShowAddModal]   = useState(false);
  const [editHead, setEditHead]           = useState(null);
  const [form, setForm]                   = useState({ name: '', description: '', openingBalance: '' });
  const [saving, setSaving]               = useState(false);

  const fetchBalances = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/financial-transactions/account-balances');
      const data = res.data.data || res.data || [];
      setBalances(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load balances');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAccountHeads = useCallback(async () => {
    try {
      const res = await axios.get('/account-heads/category/PAYMENT_ACCOUNT');
      const data = res.data.data || res.data || [];
      setAccountHeads(Array.isArray(data) ? data : []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchBalances();
    fetchAccountHeads();
  }, [fetchBalances, fetchAccountHeads]);

  const fetchAccountTransactions = async (accountHeadId) => {
    setTxnLoading(true);
    try {
      const res = await axios.get(`/financial-transactions/payment-account/${accountHeadId}`);
      const data = res.data.data || res.data || [];
      setTransactions(Array.isArray(data) ? data.slice(0, 30) : []);
    } catch (e) {
      setTransactions([]);
    } finally {
      setTxnLoading(false);
    }
  };

  const handleSelectAccount = (bal) => {
    if (selectedAccount?.accountHeadId === bal.accountHeadId) {
      setSelected(null);
      setTransactions([]);
    } else {
      setSelected(bal);
      fetchAccountTransactions(bal.accountHeadId);
    }
  };

  const openAdd = () => {
    setEditHead(null);
    setForm({ name: '', description: '', openingBalance: '' });
    setShowAddModal(true);
  };

  const openEdit = (head) => {
    setEditHead(head);
    setForm({ name: head.name, description: head.description || '', openingBalance: '' });
    setShowAddModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description, category: 'PAYMENT_ACCOUNT', active: true };
      if (editHead) {
        await axios.put(`/account-heads/${editHead.id}`, payload);
      } else {
        // Create the account head
        const res = await axios.post('/account-heads', payload);
        const newHead = res.data.data || res.data;
        // Record opening balance as an INCOME transaction if provided
        if (form.openingBalance && parseFloat(form.openingBalance) > 0) {
          await axios.post('/financial-transactions', {
            transactionType:      'INCOME',
            transactionDate:      new Date().toISOString().split('T')[0],
            amount:               String(form.openingBalance),
            paymentAccountHeadId: newHead.id,
            notes:                `Opening balance for ${form.name}`,
          });
        }
      }
      toast.success(editHead ? 'Account updated successfully' : 'Payment account created successfully');
      setShowAddModal(false);
      fetchBalances();
      fetchAccountHeads();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const totalBalance = balances.reduce((s, b) => s + parseFloat(b.balance || 0), 0);

  return (
    <div className="cashflow-page">
      <div className="page-header">
        <div>
          <h1>Cash &amp; Bank Balances</h1>
          <p>Live balances across all payment accounts</p>
        </div>
        <button className="btn-primary" onClick={openAdd}>+ Add Account</button>
      </div>

      {/* Total balance banner */}
      <div className="cashflow-total">
        <span className="cashflow-total-label">Total Available Balance</span>
        <span className="cashflow-total-value">{formatCurrency(totalBalance)}</span>
      </div>

      {loading ? (
        <div className="loading">Loading balances…</div>
      ) : balances.length === 0 ? (
        <div className="cashflow-empty">
          <p>No payment accounts found. Add a Cash or Bank account to get started.</p>
          <button className="btn-primary" onClick={openAdd}>+ Add Account</button>
        </div>
      ) : (
        <div className="cashflow-cards">
          {balances.map(bal => {
            const head = accountHeads.find(h => h.id === bal.accountHeadId);
            const isSelected = selectedAccount?.accountHeadId === bal.accountHeadId;
            const bal_num = parseFloat(bal.balance || 0);
            return (
              <div
                key={bal.accountHeadId}
                className={`cashflow-card ${isSelected ? 'cashflow-card-selected' : ''}`}
                onClick={() => handleSelectAccount(bal)}
              >
                <div className="cashflow-card-header">
                  <span className="cashflow-card-icon">{accountIcon(bal.accountName)}</span>
                  <div className="cashflow-card-title">{bal.accountName}</div>
                  <button
                    className="cashflow-edit-btn"
                    onClick={e => { e.stopPropagation(); head && openEdit(head); }}
                    title="Edit account"
                  >✏️</button>
                </div>
                <div className={`cashflow-card-balance ${bal_num < 0 ? 'negative' : ''}`}>
                  {formatCurrency(bal_num)}
                </div>
                <div className="cashflow-card-meta">
                  <span className="cashflow-in">↑ {formatCurrency(bal.inflow)}</span>
                  <span className="cashflow-out">↓ {formatCurrency(bal.outflow)}</span>
                </div>
                {isSelected && <div className="cashflow-card-arrow">▼ Transactions</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Transaction drill-down */}
      {selectedAccount && (
        <div className="cashflow-txn-panel">
          <h2 className="section-title">
            {accountIcon(selectedAccount.accountName)} {selectedAccount.accountName} — Recent Transactions
          </h2>
          {txnLoading ? (
            <div className="loading">Loading…</div>
          ) : transactions.length === 0 ? (
            <div className="no-data-panel">No transactions recorded for this account yet.</div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Voucher</th>
                    <th>Type</th>
                    <th>Party / Notes</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(t => {
                    const isIn = t.transactionType === 'INCOME';
                    return (
                      <tr key={t.id}>
                        <td>{formatDate(t.transactionDate)}</td>
                        <td className="voucher-no">{t.voucherNo}</td>
                        <td>
                          <span className="badge" style={{ background: TYPE_COLOR[t.transactionType] + '20', color: TYPE_COLOR[t.transactionType] }}>
                            {TYPE_LABELS[t.transactionType] || t.transactionType}
                          </span>
                        </td>
                        <td>{t.customerName || t.vendorName || t.employeeName || t.payeeName || t.notes || '—'}</td>
                        <td className={`amount text-right ${isIn ? 'txn-credit' : 'txn-debit'}`}>
                          {isIn ? '+' : '-'}{formatCurrency(t.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add / Edit modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddModal(false)}>
          <div className="edit-modal" style={{ maxWidth: 420 }}>
            <div className="edit-modal-header">
              <h2>{editHead ? 'Edit Account' : 'Add Payment Account'}</h2>
              <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="edit-modal-body">
              <div className="form-group">
                <label>Account Name *</label>
                <input type="text" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. HDFC Current A/c, Petty Cash" required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input type="text" value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional — bank name, account no. etc." />
              </div>
              {!editHead && (
                <div className="form-group">
                  <label>Opening Balance</label>
                  <input type="number" value={form.openingBalance}
                    onChange={e => setForm(p => ({ ...p, openingBalance: e.target.value }))}
                    placeholder="0.00 — leave blank if zero" step="0.01" min="0" />
                  <small style={{ color: '#888', fontSize: 12, marginTop: 4, display: 'block' }}>
                    Records the current amount in this account as an opening entry
                  </small>
                </div>
              )}
              <div className="edit-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editHead ? 'Update' : 'Add Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CashFlow;
